import { useState } from 'react';
import { createTargetAccount } from '../api/client';
import { X, ChevronDown, ChevronRight } from 'lucide-react';

const SERVICE_LINES = [{ value: 'DATA_LABELING', label: 'Data labeling' }];
const REGIONS = [{ value: 'US', label: 'US' }];

const RESOLVER_LABEL = {
  resolved: 'Resolved',
  unresolved: 'Unresolved',
  not_attempted: 'Not attempted',
};

function ResolverBadge({ domain, state }) {
  return (
    <span className={`resolver-badge resolver-${state}`}>
      {domain}: {RESOLVER_LABEL[state] || state}
    </span>
  );
}

const DEFAULT_LOOKBACK_DAYS = 180;

export default function AddAccountModal({ onClose, onAdded }) {
  const [companyName, setCompanyName] = useState('');
  const [companyDomain, setCompanyDomain] = useState('');
  const [serviceLine, setServiceLine] = useState(SERVICE_LINES[0].value);
  const [region, setRegion] = useState(REGIONS[0].value);
  const [lookbackDays, setLookbackDays] = useState(String(DEFAULT_LOOKBACK_DAYS));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [cik, setCik] = useState('');
  const [huggingfaceOrg, setHuggingfaceOrg] = useState('');
  const [githubOrg, setGithubOrg] = useState('');
  const [arxivQuery, setArxivQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // set on success — shows resolver feedback before closing

  const canSubmit = companyName.trim() && companyDomain.trim() && !submitting;

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const days = parseInt(lookbackDays, 10);
    createTargetAccount({
      company_name: companyName.trim(),
      company_domain: companyDomain.trim(),
      service_line: serviceLine,
      region,
      lookback_days: Number.isFinite(days) && days > 0 ? days : undefined,
      cik: cik.trim() || undefined,
      huggingface_org: huggingfaceOrg.trim() || undefined,
      github_org: githubOrg.trim() || undefined,
      arxiv_query: arxivQuery.trim() || undefined,
    })
      .then((res) => {
        setResult(res);
        onAdded?.();
      })
      .catch((err) => setError(err.message))
      .finally(() => setSubmitting(false));
  }

  const resolverStatus = result?.target_account?.resolver_status;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {!result && (
          <form onSubmit={handleSubmit}>
            <div className="modal-head">
              <h3>Add target company</h3>
              <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><X size={18} aria-hidden="true" /></button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label htmlFor="add-account-name">Company name *</label>
                <input
                  id="add-account-name"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Anthropic"
                  autoFocus
                  required
                />
              </div>
              <div className="modal-field">
                <label htmlFor="add-account-domain">Domain *</label>
                <input
                  id="add-account-domain"
                  type="text"
                  value={companyDomain}
                  onChange={(e) => setCompanyDomain(e.target.value)}
                  placeholder="anthropic.com"
                  required
                />
              </div>
              <div className="modal-field-row">
                <div className="modal-field">
                  <label htmlFor="add-account-service-line">Service line</label>
                  <select
                    id="add-account-service-line"
                    value={serviceLine}
                    onChange={(e) => setServiceLine(e.target.value)}
                  >
                    {SERVICE_LINES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="modal-field">
                  <label htmlFor="add-account-region">Region</label>
                  <select id="add-account-region" value={region} onChange={(e) => setRegion(e.target.value)}>
                    {REGIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-field">
                <label htmlFor="add-account-lookback">Lookback window (days)</label>
                <input
                  id="add-account-lookback"
                  type="number"
                  min="1"
                  max="1095"
                  value={lookbackDays}
                  onChange={(e) => setLookbackDays(e.target.value)}
                />
                <span className="modal-hint">How far back the first check looks for funding filings and research activity.</span>
              </div>

              <button
                type="button"
                className="advanced-toggle"
                onClick={() => setAdvancedOpen((v) => !v)}
              >
                {advancedOpen ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />} Advanced options
              </button>
              {advancedOpen && (
                <div className="modal-advanced">
                  <div className="modal-field">
                    <label htmlFor="add-account-cik">SEC CIK</label>
                    <input
                      id="add-account-cik"
                      type="text"
                      value={cik}
                      onChange={(e) => setCik(e.target.value)}
                      placeholder="Auto-resolved from name if left blank"
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="add-account-hf">Hugging Face org</label>
                    <input
                      id="add-account-hf"
                      type="text"
                      value={huggingfaceOrg}
                      onChange={(e) => setHuggingfaceOrg(e.target.value)}
                      placeholder="e.g. anthropic"
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="add-account-arxiv">arXiv query</label>
                    <input
                      id="add-account-arxiv"
                      type="text"
                      value={arxivQuery}
                      onChange={(e) => setArxivQuery(e.target.value)}
                      placeholder="Defaults to company name if left blank"
                    />
                    <span className="modal-hint">Search phrase used to match this company's papers on arXiv.</span>
                  </div>
                  <div className="modal-field">
                    <label htmlFor="add-account-github">GitHub org</label>
                    <input
                      id="add-account-github"
                      type="text"
                      value={githubOrg}
                      onChange={(e) => setGithubOrg(e.target.value)}
                      placeholder="e.g. anthropics"
                    />
                    <span className="modal-hint">Captured for later — targeted GitHub collection isn't wired up yet.</span>
                  </div>
                </div>
              )}

              {error && <div className="modal-error">{error}</div>}
            </div>
            <div className="modal-foot">
              <button type="button" className="btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn primary" disabled={!canSubmit}>
                {submitting ? 'Adding…' : 'Add target company'}
              </button>
            </div>
          </form>
        )}

        {result && (
          <>
            <div className="modal-head">
              <h3>{result.target_account.company_name} added</h3>
              <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><X size={18} aria-hidden="true" /></button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <span className="modal-label">Identifier resolution</span>
                <div className="resolver-badges">
                  {resolverStatus && (
                    <>
                      <ResolverBadge domain="Funding" state={resolverStatus.funding} />
                      <ResolverBadge domain="Research" state={resolverStatus.research} />
                    </>
                  )}
                </div>
              </div>
              <div className="modal-field">
                <span className="modal-label">First check</span>
                {Object.entries(result.run || {}).map(([domain, r]) => (
                  <div className="run-result-row" key={domain}>
                    <span className="run-result-domain">{domain}</span>
                    <span className="run-result-detail">
                      {r.error
                        ? r.error
                        : `${r.persisted ?? 0} new signal${r.persisted === 1 ? '' : 's'}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
